import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Mail, Phone, Building2, Users, FileText, FileSpreadsheet, Settings2, BadgeCheck, Compass, CloudSun, Layers, GraduationCap, Sparkles, Heart, Wrench, BrainCircuit, ShieldAlert } from 'lucide-react';
import { OrgImportWizard } from '@/components/client/OrgImportWizard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { api } from '@/lib/api';
import EditClientDialog from '@/components/admin/EditClientDialog';
import { USER_LIMIT_TIER_OPTIONS } from '@/components/admin/NewClientDialog';
import SuiteRequestDialog from '@/components/admin/SuiteRequestDialog';
import BillingTab from '@/components/admin/BillingTab';
import CompanyProfileForm from '@/components/shared/CompanyProfileForm';
import ClientBrandingSection from '@/components/admin/ClientBrandingSection';
import OrgDepartmentCard from '@/components/shared/org/OrgDepartmentCard';
import OrgDepartmentSheet from '@/components/shared/org/OrgDepartmentSheet';
import { HScrollArea } from '@/components/shared/HScrollArea';
import { useApp } from '@/context/AppContext';

const SUITE_ICONS = {
  talentmap: Compass, climara: CloudSun, talentcompetency: Layers, talenttrain: GraduationCap,
  talentcheck: BadgeCheck, talentstart: Sparkles, talentheart: Heart, talentfix: Wrench,
  intelligence: BrainCircuit,
};

export default function AdminClientProfile() {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isAdvisor = user?.role === 'hm_advisor';
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [allSuites, setAllSuites] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [suiteReqOpen, setSuiteReqOpen] = useState(false);
  const [draftSuites, setDraftSuites] = useState([]);
  const [draftContracted, setDraftContracted] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [pendingConfig, setPendingConfig] = useState([]);
  // UAT Finding 10 — client Departments tab embedded in the profile page.
  const [orgDepartments, setOrgDepartments] = useState([]);
  const [orgSheetOpen, setOrgSheetOpen] = useState(false);
  const [orgSheetDept, setOrgSheetDept] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  // Phase 2C — additional advisors management (hm_admin)
  const [advBusy, setAdvBusy] = useState(false);
  const [advPick, setAdvPick] = useState('');
  // P0 2026-06 (G/I) — administrative continuity: count ACTIVE Client Admins.
  const [clientAdminCount, setClientAdminCount] = useState(null);
  useEffect(() => {
    api.get('/admin/users', { params: { company_id: id, role: 'client_admin', page: 1, page_size: 100 } })
      .then((r) => {
        const rows = r.data?.items || [];
        setClientAdminCount(rows.filter((u) => !['disabled', 'inactive'].includes(u.status)).length);
      })
      .catch(() => {});
  }, [id]);
  const saveAdvisors = async (nextIds) => {
    setAdvBusy(true);
    try {
      const { data } = await api.patch(`/admin/clients/${id}/advisors`, { additional_advisor_ids: nextIds });
      setProfile((p) => ({ ...p, company: { ...p.company, additional_advisor_ids: data.additional_advisor_ids } }));
      toast.success(i18n.language?.startsWith('es') ? 'Asesores actualizados' : 'Advisors updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (i18n.language?.startsWith('es') ? 'No se pudo actualizar' : 'Update failed'));
    } finally { setAdvBusy(false); }
  };
  const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const TIER_LABEL = Object.fromEntries(USER_LIMIT_TIER_OPTIONS.map((o) => [o.code, o]));
  const tierLabel = (code) => {
    const o = TIER_LABEL[code || 'up_to_25'];
    if (!o) return code || '—';
    return i18n.language?.startsWith('es') ? o.label_es : o.label_en;
  };

  const load = async () => {
    try {
      const [p, suites, cons, pendingCfg] = await Promise.all([
        adminApi.getClientProfile(id),
        adminApi.listAllSuites(),
        adminApi.listConsultants().catch(() => []),
        adminApi.listConfigChangeRequests({ company_id: id, status: 'pending' }).catch(() => []),
      ]);
      setProfile(p);
      setAllSuites(suites);
      setConsultants(cons);
      setPendingConfig(pendingCfg || []);
    } catch (e) {
      toast.error(t('clientProfile.loadError', { defaultValue: 'Failed to load client profile' }));
      navigate('/admin/clients');
    }
  };
  useEffect(() => { if (id) load(); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // UAT Finding 10 — pull the Organizational Workspace list scoped to this client.
  const loadOrg = async () => {
    if (!id) return;
    try {
      const rows = await adminApi.listDepartments(id, false);
      setOrgDepartments(rows || []);
    } catch { /* handled by table already */ }
  };
  useEffect(() => { loadOrg(); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openManage = () => {
    // hm_advisor → opens the suite-activation request flow (Sesión B WF2).
    if (isAdvisor) { setSuiteReqOpen(true); return; }
    const active = profile?.company?.suite_codes || [];
    setDraftSuites(active);
    setDraftContracted([...new Set([...(profile?.company?.contracted_suites || []), ...active])]);
    setManageOpen(true);
  };

  // Phase 3 — contracted vs active. Active ⊆ contracted always.
  const toggleSuite = (code) => {
    setDraftSuites((prev) => {
      if (prev.includes(code)) return prev.filter(c => c !== code);
      setDraftContracted((c) => c.includes(code) ? c : [...c, code]);
      return [...prev, code];
    });
  };
  const toggleContracted = (code) => {
    setDraftContracted((prev) => {
      if (prev.includes(code)) {
        setDraftSuites((a) => a.filter(c => c !== code));
        return prev.filter(c => c !== code);
      }
      return [...prev, code];
    });
  };

  const saveSuites = async () => {
    setSaving(true);
    try {
      const result = await adminApi.updateClientSuites(id, draftSuites, draftContracted);
      toast.success(t('clientProfile.suitesSaved', { defaultValue: 'Suites updated' }));
      setManageOpen(false);
      setProfile((prev) => prev ? { ...prev, company: result.company } : prev);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const company = profile?.company;
  const evaluations = profile?.evaluations || [];
  const users = profile?.users || [];
  // Phase 3 — contracted (entitled) vs active suites.
  const activeCodes = company?.suite_codes || [];
  const contractedCodes = [...new Set([...(company?.contracted_suites || []), ...activeCodes])];
  const contractedInactive = contractedCodes.filter((c) => !activeCodes.includes(c));

  const grouped = useMemo(() => {
    const m = new Map();
    evaluations.forEach((e) => {
      const arr = m.get(e.suite_code) || [];
      arr.push(e);
      m.set(e.suite_code, arr);
    });
    return Array.from(m.entries());
  }, [evaluations]);

  if (!profile) {
    return <div className="p-8 text-muted-foreground" data-testid="client-profile-loading">{t('common.loading', { defaultValue: 'Loading…' })}</div>;
  }

  return (
    <>
      <Topbar
        title={company?.name || '—'}
        subtitle={t('clientProfile.subtitle', { defaultValue: 'Client profile · suites · users · evaluation history' })}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild data-testid="client-profile-back">
              <Link to="/admin/clients"><ArrowLeft className="h-4 w-4 mr-1" />{t('clientProfile.backToClients', { defaultValue: 'Back to Clients' })}</Link>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="client-profile-edit">
              <Settings2 className="h-4 w-4 mr-1.5" />{t('clientProfile.editTitle', { defaultValue: 'Edit configuration' })}
            </Button>
            <Button size="sm" onClick={openManage} data-testid="client-profile-manage-suites">
              <Settings2 className="h-4 w-4 mr-1.5" />{t('pages.clients.manageSuites', { defaultValue: 'Manage suites' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Identity card */}
        <Card className="p-6 shadow-card" data-testid="client-profile-identity">
          <div className="flex flex-wrap items-start gap-6">
            <div className="h-16 w-16 rounded-2xl bg-gradient-primary text-white flex items-center justify-center font-display font-bold text-xl shadow-soft shrink-0">
              {company?.logo || (company?.name || '').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={company?.status === 'active' ? 'default' : 'outline'}>{company?.status ? t(`common.statusValues.${company.status}`, { defaultValue: company.status }) : '—'}</Badge>
                {company?.plan && <Badge variant="outline" className="uppercase text-xs">{company.plan}</Badge>}
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight mt-2 flex items-center gap-2">
                {company?.name}
                {pendingConfig.length > 0 && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          data-testid="client-profile-governance-dot"
                          className="inline-flex items-center text-warning hover:opacity-70 transition-opacity"
                          onClick={() => navigate(`/admin/leads?type=config_change&status=pending&company_id=${id}`)}
                        >
                          <ShieldAlert className="h-5 w-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {t('pages.clients.governancePendingTooltip', { defaultValue: 'Configuration changes awaiting HM Admin approval' })} ({pendingConfig.length})
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">{company?.sector || '—'} · {company?.country || '—'}</p>
              {/* Contact block — stacked rows so long values never collide (Founder UAT) */}
              <div className="space-y-2 mt-4 text-sm">
                {company?.contact_email && (
                  <div className="flex items-start gap-2 text-muted-foreground min-w-0" data-testid="client-contact-email">
                    <Mail className="h-4 w-4 mt-0.5 shrink-0" /><span className="break-all">{company.contact_email}</span>
                  </div>
                )}
                {company?.contact_phone && (
                  <div className="flex items-start gap-2 text-muted-foreground min-w-0" data-testid="client-contact-phone">
                    <Phone className="h-4 w-4 mt-0.5 shrink-0" /><span className="break-words">{company.contact_phone}</span>
                  </div>
                )}
                {company?.tax_id && (
                  <div className="flex items-start gap-2 text-muted-foreground min-w-0" data-testid="client-contact-taxid">
                    <Building2 className="h-4 w-4 mt-0.5 shrink-0" /><span className="break-words">{company.tax_id}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 min-w-[300px]">
              <Stat label={t('clientProfile.users', { defaultValue: 'Users' })} value={profile.stats.users_total} icon={Users} />
              <Stat label={t('phase3.clients.collaborators', { defaultValue: 'Collaborators' })} value={profile.stats.collaborators_total ?? '—'} icon={Users} testId="stat-collaborators" />
              <Stat label={t('clientProfile.evaluations', { defaultValue: 'Evaluations' })} value={profile.stats.evaluations_total} icon={FileText} />
              <Stat label={t('clientProfile.activeSuites', { defaultValue: 'Active suites' })} value={activeCodes.length} icon={Layers} />
            </div>
          </div>
        </Card>

        {/* Iter 31 (R1) — Account configuration: tier · consultant · suites */}
        <Card className="p-6 shadow-card" data-testid="client-profile-account-config">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">
                {t('clientProfile.accountConfig', { defaultValue: 'Account configuration' })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('clientProfile.accountConfigSub', { defaultValue: 'Plan tier, assigned consultant and contracted suites.' })}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="client-config-edit">
              <Settings2 className="h-4 w-4 mr-1.5" />{t('clientProfile.editTitle', { defaultValue: 'Edit configuration' })}
            </Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('clientProfile.userLimit', { defaultValue: 'Maximum users' })}</p>
              <p className="font-semibold mt-1" data-testid="cfg-user-limit">{tierLabel(company?.user_limit_tier)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('clientProfile.assignedConsultant', { defaultValue: 'Assigned consultant' })}</p>
              <p className="font-semibold mt-1" data-testid="cfg-consultant">
                {consultants.find((c) => c.id === company?.assigned_consultant_id)?.name
                  || (i18n.language?.startsWith('es') ? 'Sin asignar' : 'Unassigned')}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('clientProfile.contractedSuites', { defaultValue: 'Contracted suites' })}</p>
              <p className="font-semibold mt-1 tabular-nums" data-testid="cfg-suites-count">
                {contractedCodes.length}
                <span className="text-xs text-muted-foreground font-normal ml-1.5">
                  ({activeCodes.length} {t('phase3.suites.activeShort', { defaultValue: 'active' })})
                </span>
              </p>
            </div>
          </div>
          {/* Sprint Governance — pending config change chips */}
          {pendingConfig.length > 0 && (
            <div className="mt-4 pt-3 border-t border-warning/30 space-y-1.5" data-testid="cfg-pending-changes">
              <p className="text-xs uppercase tracking-wider font-semibold text-warning">
                {t('governance.pendingChanges', { defaultValue: 'Pending approval' })}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {pendingConfig.flatMap((r) => (r.changes || []).map((ch) => (
                  <Badge
                    key={`${r.id}-${ch.field}`}
                    variant="outline"
                    className="bg-warning/10 text-warning border-warning/30 text-xs"
                    data-testid={`cfg-pending-${ch.field}`}
                  >
                    {t(`governance.fields.${ch.field}`, { defaultValue: ch.field })} → {Array.isArray(ch.to) ? ch.to.length : String(ch.to ?? '—')}
                    <span className="ml-1 opacity-70">· {r.requested_by_name}</span>
                  </Badge>
                )))}
              </div>
            </div>
          )}
        </Card>

        {/* P0 2026-06 (I) — administrative continuity warning (non-blocking) */}
        {clientAdminCount !== null && clientAdminCount <= 1 && (
          <Card className={`p-3 shadow-card border-l-4 ${clientAdminCount === 0 ? 'border-l-red-600 bg-red-50/50' : 'border-l-amber-500 bg-amber-50/50'}`}
                data-testid="client-admin-continuity-warning">
            <p className="text-sm font-medium">
              {clientAdminCount === 0
                ? (i18n.language?.startsWith('es')
                  ? 'Tenant sin administrador activo — CTH Admin debe designar un nuevo Administrador de Cliente (Usuarios → Crear usuario).'
                  : 'Tenant has NO active Client Admin — CTH Admin must appoint a new Client Admin (Users → Create user).')
                : (i18n.language?.startsWith('es')
                  ? 'Continuidad administrativa en riesgo — este cliente tiene un único administrador activo. Se recomienda designar un segundo Administrador de Cliente.'
                  : 'Administrative continuity at risk — this client has a single active admin. A second Client Admin is recommended.')}
            </p>
          </Card>
        )}

        {/* Phase 2C — CTH Advisors: PRIMARY (responsible) vs ADDITIONAL (access) */}
        <Card className="p-6 shadow-card" data-testid="client-advisors-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">
                {i18n.language?.startsWith('es') ? 'Asesores CTH' : 'CTH Advisors'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {i18n.language?.startsWith('es')
                  ? 'El asesor principal es el responsable del cliente; los asesores adicionales tienen acceso autorizado sin ser los responsables principales.'
                  : 'The primary advisor is responsible for the client; additional advisors have authorized access without being the primary responsible.'}
              </p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 flex-wrap" data-testid="client-primary-advisor">
              <Badge className="bg-primary text-primary-foreground text-xs uppercase tracking-wider">
                {i18n.language?.startsWith('es') ? 'Principal' : 'Primary'}
              </Badge>
              <span className="font-semibold">
                {consultants.find((c) => c.id === company?.assigned_consultant_id)?.name
                  || (i18n.language?.startsWith('es') ? 'Sin asignar' : 'Unassigned')}
              </span>
              {!isAdvisor && (
                <span className="text-xs text-muted-foreground">
                  {i18n.language?.startsWith('es')
                    ? '(se cambia desde "Editar configuración" — flujo gobernado)'
                    : '(changed via "Edit configuration" — governed flow)'}
                </span>
              )}
            </div>
            {(company?.additional_advisor_ids || []).map((aid) => (
              <div key={aid} className="flex items-center gap-2 flex-wrap" data-testid={`client-additional-advisor-${aid}`}>
                <Badge variant="outline" className="text-xs uppercase tracking-wider text-muted-foreground">
                  {i18n.language?.startsWith('es') ? 'Adicional' : 'Additional'}
                </Badge>
                <span className="font-medium">{consultants.find((c) => c.id === aid)?.name || aid}</span>
                {!isAdvisor && (
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs text-muted-foreground hover:text-destructive"
                          disabled={advBusy} data-testid={`client-remove-advisor-${aid}`}
                          onClick={() => saveAdvisors((company?.additional_advisor_ids || []).filter((x) => x !== aid))}>
                    {i18n.language?.startsWith('es') ? 'Quitar' : 'Remove'}
                  </Button>
                )}
              </div>
            ))}
            {(company?.additional_advisor_ids || []).length === 0 && (
              <p className="text-xs text-muted-foreground italic" data-testid="client-no-additional-advisors">
                {i18n.language?.startsWith('es') ? 'Sin asesores adicionales.' : 'No additional advisors.'}
              </p>
            )}
            {!isAdvisor && (
              <div className="flex items-center gap-2 pt-2 border-t border-border mt-3">
                <select
                  className="h-9 rounded-md border border-border bg-background px-2 text-sm min-w-56"
                  value={advPick}
                  onChange={(e) => setAdvPick(e.target.value)}
                  data-testid="client-add-advisor-select"
                >
                  <option value="">
                    {i18n.language?.startsWith('es') ? 'Agregar asesor adicional…' : 'Add additional advisor…'}
                  </option>
                  {consultants
                    .filter((c) => c.id !== company?.assigned_consultant_id
                      && !(company?.additional_advisor_ids || []).includes(c.id))
                    .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <Button size="sm" disabled={!advPick || advBusy} data-testid="client-add-advisor-btn"
                        onClick={() => { saveAdvisors([...(company?.additional_advisor_ids || []), advPick]); setAdvPick(''); }}>
                  {i18n.language?.startsWith('es') ? 'Agregar' : 'Add'}
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* UAT 07/18 — shared corporate Company Profile (same component and
            data source used by the Client Admin portal). */}
        <div data-testid="admin-corporate-profile">
          <h3 className="font-display font-bold text-lg tracking-tight mb-3">
            {t('pages.companyProfile.title', { defaultValue: 'Company Profile' })}
          </h3>
          <CompanyProfileForm
            company={profile?.company}
            onSave={async (body) => {
              const result = await adminApi.updateClient(id, body);
              setProfile((p) => ({ ...p, company: result.company }));
              return result.company;
            }}
          />
        </div>

        {/* Founder Branding sprint — client hero/banner self-service (HM Admin
            full control, HM Advisor read-only). */}
        <ClientBrandingSection companyId={id} company={profile?.company} readOnly={isAdvisor} />

        {/* BUG 04 — Sub-sections moved under client profile */}
        <Card className="p-5 shadow-card" data-testid="client-profile-subsections">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">
                {i18n.language?.startsWith('es') ? 'Estructura y evaluaciones' : 'Structure & evaluations'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {i18n.language?.startsWith('es')
                  ? 'Departamentos, colaboradores y reportes de este cliente.'
                  : 'Departments, collaborators and reports for this client.'}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" data-testid="client-profile-bulk-import-btn"
                      onClick={() => setImportOpen(true)}>
                <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                {t('pages.collaborators.import.btn')}
              </Button>
              <Button variant="outline" size="sm" data-testid="client-go-departments"
                      onClick={() => navigate(`/admin/departments?company_id=${id}`)}>
                <Building2 className="h-4 w-4 mr-1.5" />
                {i18n.language?.startsWith('es') ? 'Departamentos' : 'Departments'}
              </Button>
              <Button variant="outline" size="sm" data-testid="client-go-orgboard"
                      onClick={() => navigate(`/admin/org-board?company_id=${id}`)}>
                <Building2 className="h-4 w-4 mr-1.5" />
                {i18n.language?.startsWith('es') ? 'Organigrama' : 'Org Structure Board'}
              </Button>
              <Button variant="outline" size="sm" data-testid="client-go-evaluations"
                      onClick={() => navigate(`/admin/evaluations?company_id=${id}`)}>
                <FileText className="h-4 w-4 mr-1.5" />
                {i18n.language?.startsWith('es') ? 'Evaluaciones e Informes' : 'Evaluations & Reports'}
              </Button>
            </div>
          </div>
        </Card>

        {/* UAT Finding 10 — Organizational Workspace (client Departments tab). */}
        <Card className="p-6 shadow-card" data-testid="client-profile-org-departments">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">
                {t('pages.clientDepartments.org.adminSectionTitle', { defaultValue: 'Departments' })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('pages.clientDepartments.org.adminSectionSubtitle', {
                  defaultValue: 'Real-time organizational workspace for this client — derived from the users collection.',
                })}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/departments?company_id=${id}`)}>
              <Settings2 className="h-4 w-4 mr-1.5" />
              {i18n.language?.startsWith('es') ? 'Gestionar' : 'Manage'}
            </Button>
          </div>
          {orgDepartments.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-6 text-center">
              {t('pages.clientDepartments.empty', { defaultValue: 'No departments yet.' })}
            </p>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {orgDepartments.map((d) => (
                <OrgDepartmentCard
                  key={d.id}
                  department={d}
                  onOpenSheet={(dep) => { setOrgSheetDept(dep); setOrgSheetOpen(true); }}
                  canManagePositions={false}
                  testIdPrefix="admin-dept"
                />
              ))}
            </div>
          )}
        </Card>

        {/* Suites strip — Phase 3: active vs contracted-inactive shown distinctly */}
        <Card className="p-6 shadow-card" data-testid="client-profile-suites">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg tracking-tight">{t('phase3.suites.cardTitle', { defaultValue: 'Suites' })}</h3>
              <p className="text-sm text-muted-foreground">{t('phase3.suites.cardSub', { defaultValue: 'Contracted entitlements and their activation state.' })}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div data-testid="client-profile-active-suites">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                {t('phase3.suites.activeTitle', { defaultValue: 'Active suites' })} ({activeCodes.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {activeCodes.map((code) => {
                  const meta = allSuites.find(s => s.code === code) || { name: code };
                  const Icon = SUITE_ICONS[code] || Layers;
                  return (
                    <Badge key={code} variant="secondary" className="gap-1.5 px-3 py-1.5 text-xs bg-success/10 text-success border border-success/20" data-testid={`client-suite-${code}`}>
                      <Icon className="h-3 w-3" />{meta.name}
                    </Badge>
                  );
                })}
                {!activeCodes.length && (
                  <p className="text-sm text-muted-foreground italic">{t('clientProfile.noSuites', { defaultValue: 'No suites assigned yet.' })}</p>
                )}
              </div>
            </div>
            {contractedInactive.length > 0 && (
              <div data-testid="client-profile-contracted-inactive">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                  {t('phase3.suites.contractedInactiveTitle', { defaultValue: 'Contracted — not active' })} ({contractedInactive.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {contractedInactive.map((code) => {
                    const meta = allSuites.find(s => s.code === code) || { name: code };
                    const Icon = SUITE_ICONS[code] || Layers;
                    return (
                      <Badge key={code} variant="outline" className="gap-1.5 px-3 py-1.5 text-xs text-warning border-warning/40 bg-warning/5" data-testid={`client-suite-contracted-${code}`}>
                        <Icon className="h-3 w-3" />{meta.name}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Users */}
        <Card className="p-6 shadow-card" data-testid="client-profile-users">
          <h3 className="font-display font-bold text-lg tracking-tight">{t('clientProfile.assignedUsers', { defaultValue: 'Assigned users' })}</h3>
          <p className="text-sm text-muted-foreground">{t('clientProfile.assignedUsersSub', { defaultValue: 'All users tied to this client.' })}</p>
          <HScrollArea className="mt-4" testid="client-profile-users-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                  <th className="py-2 pr-3">{t('clientProfile.name', { defaultValue: 'Name' })}</th>
                  <th className="py-2 px-3">{t('clientProfile.email', { defaultValue: 'Email' })}</th>
                  <th className="py-2 px-3">{t('clientProfile.role', { defaultValue: 'Role' })}</th>
                  <th className="py-2 pl-3">{t('clientProfile.position', { defaultValue: 'Position' })}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b last:border-0" data-testid={`client-user-${u.id}`}>
                    <td className="py-3 pr-3 font-medium">{u.name}</td>
                    <td className="py-3 px-3 text-muted-foreground">{u.email}</td>
                    <td className="py-3 px-3"><Badge variant="outline" className="text-xs">{u.role ? t(`pages.users.roles.${u.role}`, { defaultValue: u.role.replace('_', ' ') }) : '—'}</Badge></td>
                    <td className="py-3 pl-3 text-muted-foreground">{u.position || '—'}</td>
                  </tr>
                ))}
                {!users.length && (
                  <tr><td colSpan={4} className="py-6 text-center text-muted-foreground italic">{t('clientProfile.noUsers', { defaultValue: 'No users yet.' })}</td></tr>
                )}
              </tbody>
            </table>
          </HScrollArea>
        </Card>

        {/* Evaluation history grouped by suite */}
        <Card className="p-6 shadow-card" data-testid="client-profile-evaluations">
          <h3 className="font-display font-bold text-lg tracking-tight">{t('clientProfile.evaluationHistory', { defaultValue: 'Evaluation history' })}</h3>
          <p className="text-sm text-muted-foreground">{t('clientProfile.evaluationHistorySub', { defaultValue: 'Every assignment created for this client, grouped by suite.' })}</p>
          {!grouped.length && (
            <p className="text-sm text-muted-foreground italic mt-4">{t('clientProfile.noEvaluations', { defaultValue: 'No evaluations yet.' })}</p>
          )}
          <div className="space-y-6 mt-4">
            {grouped.map(([code, list]) => {
              const Icon = SUITE_ICONS[code] || Layers;
              const meta = allSuites.find(s => s.code === code);
              return (
                <div key={code}>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Icon className="h-4 w-4" /> {meta?.name || code}
                    <Badge variant="outline" className="text-xs">{list.length}</Badge>
                  </div>
                  <HScrollArea className="mt-2">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                          <th className="py-2 pr-3">{t('clientProfile.evalUser', { defaultValue: 'User' })}</th>
                          <th className="py-2 px-3">{t('clientProfile.evalStatus', { defaultValue: 'Status' })}</th>
                          <th className="py-2 px-3 tabular-nums">{t('clientProfile.evalCreated', { defaultValue: 'Created' })}</th>
                          <th className="py-2 px-3 tabular-nums">{t('clientProfile.evalDeadline', { defaultValue: 'Deadline' })}</th>
                          <th className="py-2 pl-3">{t('clientProfile.evalScore', { defaultValue: 'Score' })}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((e) => (
                          <tr key={e.id} className="border-b last:border-0">
                            <td className="py-2.5 pr-3">{e.assigned_to?.name || '—'}</td>
                            <td className="py-2.5 px-3"><Badge variant="outline" className="text-xs">{e.status ? t(`common.evalStatus.${e.status}`, { defaultValue: e.status.replace('_', ' ') }) : '—'}</Badge></td>
                            <td className="py-2.5 px-3 text-muted-foreground tabular-nums">{fmtDate(e.created_at)}</td>
                            <td className="py-2.5 px-3 text-muted-foreground tabular-nums">{fmtDate(e.deadline)}</td>
                            <td className="py-2.5 pl-3 tabular-nums">{e.report?.overall_score ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </HScrollArea>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Iter 41.38 — Lightweight commercial Billing & Subscriptions tab.
            Visible to hm_admin (read+write) and hm_advisor (read-only on
            companies in their book). Other roles never reach this page. */}
        <BillingTab companyId={company?.id} companySuiteCodes={company?.suite_codes || []} />
      </div>

      {/* Manage Suites modal — Phase 3: contracted + active per suite */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="sm:max-w-2xl lg:max-w-4xl" data-testid="manage-suites-dialog">
          <DialogHeader>
            <DialogTitle>{t('clientProfile.manageSuitesTitle', { defaultValue: 'Manage suites' })}</DialogTitle>
            <DialogDescription>{t('phase3.suites.manageDesc', { defaultValue: 'Manage contract (entitlement) and activation per suite. An active suite is always contracted.' })}</DialogDescription>
          </DialogHeader>
          <div className="py-2 max-h-[72vh] overflow-y-auto">
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-1 items-center">
              <span />
              <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground px-1">{t('phase3.suites.colContracted', { defaultValue: 'Contracted' })}</span>
              <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground px-1">{t('phase3.suites.colActive', { defaultValue: 'Active' })}</span>
              {allSuites.map((s) => {
                const Icon = SUITE_ICONS[s.code] || Layers;
                const isActive = draftSuites.includes(s.code);
                const isContracted = draftContracted.includes(s.code);
                return (
                  <React.Fragment key={s.code}>
                    <div className={`flex items-center gap-2 py-2 px-2 rounded-md ${isActive ? 'bg-success/5' : isContracted ? 'bg-warning/5' : ''}`} data-testid={`suite-toggle-${s.code}`}>
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium">{s.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{s.tagline || s.description || ''}</div>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <Checkbox checked={isContracted} onCheckedChange={() => toggleContracted(s.code)} data-testid={`suite-contracted-${s.code}`} />
                    </div>
                    <div className="flex justify-center">
                      <Checkbox checked={isActive} onCheckedChange={() => toggleSuite(s.code)} data-testid={`suite-active-${s.code}`} />
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-3">{t('phase3.suites.activeImpliesContracted', { defaultValue: 'Activating a suite automatically marks it as contracted; removing the contract also deactivates it.' })}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageOpen(false)} data-testid="manage-suites-cancel">{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={saveSuites} disabled={saving} data-testid="manage-suites-save">{saving ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save', { defaultValue: 'Save' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditClientDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        company={company}
        onSaved={(updated) => setProfile((p) => p ? { ...p, company: updated } : p)}
      />
      <SuiteRequestDialog
        open={suiteReqOpen}
        onOpenChange={setSuiteReqOpen}
        company={company}
        requesterName={user?.name}
        activeSuites={activeCodes}
        contractedSuites={contractedCodes}
      />
      <OrgImportWizard
        mode="hm"
        presetCompany={company ? { id: company.id, name: company.name } : null}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { load(); loadOrg(); }}
      />
      {/* UAT Finding 10 — admin-scope roster sheet. Admin has PATCH /admin/users/:id, so members can be moved / removed / assigned. */}
      <OrgDepartmentSheet
        open={orgSheetOpen}
        onOpenChange={setOrgSheetOpen}
        department={orgSheetDept}
        apiPrefix="admin"
        companyId={id}
        allDepartments={orgDepartments}
        canManageMembers={!isAdvisor}
        onAfterChange={loadOrg}
      />
    </>
  );
}

function Stat({ label, value, icon: Icon, testId }) {
  return (
    <Card className="p-3 shadow-card" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="font-display text-xl font-bold tabular-nums mt-1">{value}</p>
    </Card>
  );
}
